import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Edit,
  Phone,
  Plus,
  Trash2,
  X,
  Eye,
  EyeOff,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import "react-phone-number-input/style.css";
import PhoneField from "../common/PhoneField";
import { useSelector } from "react-redux";
import {
  createTwilioAccount,
  deleteTwilioAccount,
  getAllTwilioAccounts,
  testTwilioAccountConnection,
  updateTwilioAccount,
} from "../../api/twilio";
import "./TwilioPhoneInput.css";
import { hasAdminAccess } from "../../utils/roleAccess";

const TwilioAccountsManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);

  const [loading, setLoading] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState({
    account_sid: "",
    auth_token: "",
    phone_number: "",
    is_active: true,
    name: "", // Added name field for better identification
  });

  // Brand Colors
  const brandColors = {
    primary: "#575955",
    secondary: "#55C5D0",
  };

  useEffect(() => {
    if (isAdmin) {
      fetchAccounts();
    }
  }, [isAdmin]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await getAllTwilioAccounts();
      // Check if response.data is an array or if it has a results property
      const accountsData = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];

      // console.log(accountsData);
      setAccounts(accountsData);
      // toast.success("Twilio accounts loaded successfully");
    } catch (error) {
      console.error("Error fetching Twilio accounts:", error);
      toast.error(
        error?.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to load Twilio accounts"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handlePhoneChange = (value) => {
    setFormData({
      ...formData,
      phone_number: value || "",
    });
  };

  const resetForm = () => {
    setFormData({
      account_sid: "",
      auth_token: "",
      phone_number: "",
      is_active: true,
      // name: "",
    });
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone number
    if (!formData.phone_number || formData.phone_number.length < 8) {
      toast.error("Please enter a valid phone number");
      return;
    }

    try {
      setLoading(true);

      // Create payload with only the required fields
      const payload = {
        account_sid: formData.account_sid,
        auth_token: formData.auth_token,
        phone_number: formData.phone_number,
        is_active: formData.is_active,
      };

      // Add name if it exists in the backend API
      // if (formData.name) {
      //   payload.name = formData.name;
      // }

      if (editingId) {
        // Update existing account
        await updateTwilioAccount(editingId, payload);
        toast.success("Twilio account updated successfully");
      } else {
        // Create new account
        await createTwilioAccount(payload);
        toast.success("Twilio account added successfully");
      }

      // Refresh the accounts list
      fetchAccounts();
      resetForm();
    } catch (error) {
      console.error("Error saving Twilio account:", error);
      toast.error(
        error?.response?.data?.message ||
        error.response?.data?.detail ||
        (editingId ? "Failed to update account" : "Failed to add account")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account) => {
    setFormData({
      account_sid: account.account_sid,
      auth_token: account.auth_token,
      phone_number: account.phone_number,
      is_active: account.is_active,
      // name: account.name || "",
    });
    setEditingId(account.id || account.account_sid);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this Twilio account?")
    ) {
      try {
        setLoading(true);
        await deleteTwilioAccount(id);
        toast.success("Twilio account deleted successfully");
        fetchAccounts();
      } catch (error) {
        console.error("Error deleting Twilio account:", error);
        toast.error(
          error?.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to delete Twilio account"
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTestConnection = async (id) => {
    try {
      setLoading(true);
      const response = await testTwilioAccountConnection(id);

      // Check if we got a successful response
      if (response && response.status === 200) {
        toast.success("Twilio account verified successfully!");
      } else {
        toast.error("Could not verify Twilio account");
      }
    } catch (error) {
      console.error("Error verifying Twilio account:", error);
      toast.error("Failed to verify Twilio account");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Admin Access Required
          </h3>
          <p className="text-sm text-gray-600">
            You need administrator privileges to manage Twilio accounts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${brandColors.secondary}15` }}
          >
            <Phone
              className="w-5 h-5"
              style={{ color: brandColors.secondary }}
            />
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Twilio Accounts Management
            </h3>
            <p className="text-sm text-gray-600">
              Manage multiple Twilio accounts for your organization
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddForm(!showAddForm);
          }}
          className="flex items-center space-x-1 px-3 py-2 text-sm font-medium rounded-xl text-white transition-all duration-200"
          style={{ backgroundColor: brandColors.secondary }}
        >
          {showAddForm ? (
            <>
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              <span>Add Account</span>
            </>
          )}
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-8 border border-gray-100 rounded-xl p-4 bg-gray-50"
        >
          <h4 className="text-md font-medium text-gray-900 mb-4">
            {editingId ? "Edit Twilio Account" : "Add New Twilio Account"}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Account Name */}
            {/* <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Account Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter a name for this account"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl 
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                style={{ "--tw-ring-color": brandColors.secondary }}
                required
              />
            </div> */}

            {/* Account SID */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Twilio Account SID
              </label>
              <input
                type="text"
                name="account_sid"
                value={formData.account_sid}
                onChange={handleInputChange}
                placeholder="Enter Twilio Account SID"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl 
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                style={{ "--tw-ring-color": brandColors.secondary }}
                required
              />
            </div>

            {/* Auth Token */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Twilio Auth Token
              </label>
              <input
                type="password"
                name="auth_token"
                value={formData.auth_token}
                onChange={handleInputChange}
                placeholder="Enter Twilio Auth Token"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl 
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                style={{ "--tw-ring-color": brandColors.secondary }}
                required
              />
            </div>

            {/* Phone Number */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">
                Twilio Phone Number
              </label>
              <div className="phone-input-container">
                <PhoneField
                  value={formData.phone_number}
                  onChange={handlePhoneChange}
                  placeholder="Enter Twilio Phone Number"
                  className="w-full"
                  required
                />
              </div>
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label
              htmlFor="is_active"
              className="ml-2 text-sm font-medium text-gray-700"
            >
              Enable this Twilio account
            </label>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium rounded-xl text-white transition-all duration-200"
              style={{ backgroundColor: brandColors.secondary }}
            >
              {loading
                ? "Saving..."
                : editingId
                  ? "Update Account"
                  : "Add Account"}
            </button>
          </div>
        </form>
      )}

      {/* Accounts List */}
      <div className="space-y-4">
        {loading && !accounts.length ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading accounts...</p>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-gray-200 rounded-xl">
            <Phone className="w-12 h-12 mx-auto text-gray-300 mb-2" />
            <p className="text-gray-500">No Twilio accounts added yet</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              Add your first account
            </button>
          </div>
        ) : (
          accounts.map((account) => (
            <div
              key={account.id || account.account_sid}
              className="border border-gray-100 rounded-xl overflow-hidden"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleExpand(account.id || account.account_sid)}
              >
                <div className="flex items-center space-x-3">
                  <div
                    className={`w-2 h-2 rounded-full ${account.is_active ? "bg-green-500" : "bg-gray-300"
                      }`}
                  ></div>
                  <h4 className="font-medium text-gray-900">
                    {account.account_sid || "Unnamed Account"}
                  </h4>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">
                    {account.phone_number}
                  </span>
                  {expandedId === (account.id || account.account_sid) ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>

              {expandedId === (account.id || account.account_sid) && (
                <div className="p-4 border-t border-gray-100 bg-gray-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Account SID</p>
                      <p className="text-sm font-mono bg-gray-100 p-2 rounded overflow-x-auto">
                        {account.account_sid}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Auth Token</p>
                      <p className="text-sm font-mono bg-gray-100 p-2 rounded overflow-x-auto">
                        {account.auth_token.replace(/./g, "•")}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() =>
                        handleTestConnection(account.id || account.account_sid)
                      }
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-100 transition-all duration-200"
                    >
                      Test Connection
                    </button>
                    <button
                      onClick={() => handleEdit(account)}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-100 transition-all duration-200"
                    >
                      <Edit className="w-3 h-3 inline mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() =>
                        handleDelete(account.id || account.account_sid)
                      }
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-red-100 text-red-600 hover:bg-red-50 transition-all duration-200"
                    >
                      <Trash2 className="w-3 h-3 inline mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TwilioAccountsManagement;
